// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    REGISTER: '/api/auth/register',
    LOGIN: '/api/auth/login',
    PROFILE: '/api/auth/profile',
    VERIFY_EMAIL: '/api/auth/verify',
  },
  // Consultations
  CONSULTATION: {
    BASE: '/api/consultation',
    BY_ID: (id) => `/api/consultation/${id}`,
  },
  // Contacts
  CONTACT: {
    BASE: '/api/contact',
    BY_ID: (id) => `/api/contact/${id}`,
  },
  // Users
  USER: {
    BASE: '/api/users',
    BY_ID: (id) => `/api/users/${id}`,
    VERIFY: (id) => `/api/users/${id}/verify`,
    ACTIVATE: (id) => `/api/users/${id}/activate`,
    DEACTIVATE: (id) => `/api/users/${id}/deactivate`,
    SUSPEND: (id) => `/api/users/${id}/suspend`,
  },
  // Security
  SECURITY: {
    OVERVIEW: '/api/security/overview',
    EVENTS: '/api/security/events',
    TRENDS: '/api/security/trends',
    LOCKED_ACCOUNTS: '/api/security/locked-accounts',
    TOP_FAILED_TARGETS: '/api/security/top-failed-targets',
    ANOMALIES: '/api/security/anomalies',
    RESOLVE_EVENT: (id) => `/api/security/events/${id}/resolve`,
    UNLOCK_ACCOUNT: (userId) => `/api/security/accounts/${userId}/unlock`,
  },
};

// User Roles
export const USER_ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
};

// Account Status
export const ACCOUNT_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  DEACTIVATED: 'deactivated',
};

// Consultation Statuses
export const CONSULTATION_STATUS = {
  PENDING: 'pending',
  CONTACTED: 'contacted',
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

// Contact Statuses
export const CONTACT_STATUS = {
  NEW: 'new',
  READ: 'read',
  REPLIED: 'replied',
  ARCHIVED: 'archived',
};

// Service Types
export const SERVICE_TYPES = {
  DEVELOPMENT: 'development',
  DESIGN: 'design',
  SUPPORT: 'support',
  CONSULTING: 'consulting',
  DOMAINS: 'domains',
  HOSTING: 'hosting',
  OTHER: 'other',
};

// Pagination Defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  ITEMS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
};

// LocalStorage Keys
export const STORAGE_KEYS = {
  TOKEN: 'authToken',
  USER: 'user',
  THEME: 'theme',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized. Please login again.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TOKEN_EXPIRED: 'Your session has expired. Please login again.',
  ACCOUNT_LOCKED: 'Your account has been locked due to suspicious activity. Please contact an administrator to regain access.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful! Redirecting...',
  REGISTER_SUCCESS: 'Registration successful! Please check your email for verification.',
  PROFILE_UPDATED: 'Profile updated successfully!',
  CONSULTATION_CREATED: 'Consultation request submitted successfully!',
  CONTACT_CREATED: 'Message sent successfully!',
  STATUS_UPDATED: 'Status updated successfully!',
  DELETED: 'Deleted successfully!',
};

// Security Event Types
export const SECURITY_EVENT_TYPES = {
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',
  PASSWORD_RESET_REQUEST: 'password_reset_request',
  PASSWORD_RESET_SUCCESS: 'password_reset_success',
  ACCOUNT_LOCKOUT: 'account_lockout',
  ACCOUNT_UNLOCK: 'account_unlock',
  SUSPICIOUS_ACTIVITY: 'suspicious_activity',
  BRUTE_FORCE_ATTEMPT: 'brute_force_attempt',
  UNUSUAL_IP_LOGIN: 'unusual_ip_login',
};

// Security Event Type Labels
export const SECURITY_EVENT_LABELS = {
  login_success: 'Login Success',
  login_failed: 'Login Failed',
  logout: 'Logout',
  password_reset_request: 'Password Reset Request',
  password_reset_success: 'Password Reset Success',
  account_lockout: 'Account Lockout',
  account_unlock: 'Account Unlock',
  suspicious_activity: 'Suspicious Activity',
  brute_force_attempt: 'Brute Force Attempt',
  unusual_ip_login: 'Unusual IP Login',
};

// Security Severity Levels
export const SECURITY_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

// Security Severity Colors
export const SECURITY_SEVERITY_COLORS = {
  low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
};

// Analytics
export const ANALYTICS_ENDPOINTS = {
  OVERVIEW: '/api/analytics/overview',
  ACTIVE_VISITORS: '/api/analytics/active-visitors',
  VISITORS: '/api/analytics/visitors',
  VISITOR_TRENDS: '/api/analytics/visitors/trends',
  LEADS: '/api/analytics/leads',
  QUOTE_FUNNEL: '/api/analytics/quote-funnel',
  FORM_DRAFTS: '/api/analytics/form-drafts',
  TRACK_PAGEVIEW: '/api/analytics/track/pageview',
  TRACK_FUNNEL: '/api/analytics/track/funnel',
  TRACK_DRAFT: '/api/analytics/track/draft',
};

export const FUNNEL_EVENT_TYPES = {
  FORM_VIEWED: 'form_viewed',
  FORM_STARTED: 'form_started',
  FIELD_FILLED: 'field_filled',
  FORM_SUBMITTED: 'form_submitted',
  FORM_ABANDONED: 'form_abandoned',
};

export const DRAFT_STATUS = {
  IN_PROGRESS: 'in_progress',
  ABANDONED: 'abandoned',
  COMPLETED: 'completed',
};
